/**
 * BlogAuth — /auth on the SocialPulse.blog site chrome.
 *
 * Distraction-free sign-in / sign-up page that matches the .blog visual
 * language (Lora serif, teal accent, cream background). Reuses the
 * existing AuthContext so we get Google + email-magic-link with zero
 * duplication.
 *
 * Design guardrails:
 *   • No main-app navigation (kept out of the header)
 *   • Two flows only: Google (one click) + Email (magic link)
 *   • Post sign-in → return to `?next=` if present, else `/blog`
 */
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Globe, Loader2, Mail, ArrowRight, CheckCircle2, AlertCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const GOOGLE_ICON =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'>
      <path fill='#FFC107' d='M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z'/>
      <path fill='#FF3D00' d='M6.3 14.7l6.6 4.8C14.5 15.1 18.9 12 24 12c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z'/>
      <path fill='#4CAF50' d='M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.4 34.9 26.8 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z'/>
      <path fill='#1976D2' d='M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C40.7 35.6 44 30.3 44 24c0-1.2-.1-2.3-.4-3.5z'/>
    </svg>`
  );


function useNextParam() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  return params.get("next") || "/blog";
}


export default function BlogAuth() {
  const { user, signInWithGoogleProvider, signInWithGooglePopupProvider, sendEmailLink } = useAuth();
  const navigate = useNavigate();
  const nextTargetFromUrl = useNextParam();

  // Persist the next-target across any URL churn during the auth flow
  // (Firebase redirect round-trip, magic-link click on another device,
  // etc.). URL params get flaky — sessionStorage doesn't. We stash it
  // under BOTH keys because AuthContext also reads its own key after
  // the Google redirect returns.
  useEffect(() => {
    if (nextTargetFromUrl && nextTargetFromUrl !== "/blog") {
      try {
        window.sessionStorage.setItem("sp_auth_next", nextTargetFromUrl);
        window.sessionStorage.setItem("sp_google_redirect_next", nextTargetFromUrl);
      } catch { /* noop */ }
    }
  }, [nextTargetFromUrl]);

  // Listen for redirect failures (unauthorized-domain, backend rejection…).
  // AuthContext dispatches "sp:google-redirect-error" after consuming the
  // redirect result — we surface a friendly banner instead of a silent
  // loop.
  useEffect(() => {
    const handler = (e) => {
      const code = e?.detail?.code || "";
      const msg =
        code === "auth/unauthorized-domain"
          ? `This site (${typeof window !== "undefined" ? window.location.hostname : ""}) isn't in the Firebase project's authorized domains yet. An admin needs to add it.`
        : code === "auth/operation-not-allowed"
          ? "Google sign-in isn't enabled in the Firebase project."
        : code === "auth/network-request-failed"
          ? "Network error contacting Google. Please try again."
        : code === "backend"
          ? (e?.detail?.message || "Our server rejected the sign-in token. Please try again.")
        : (e?.detail?.message || "Google sign-in didn't complete. Please try again.");
      setError(msg);
      setMode("error");
    };
    window.addEventListener("sp:google-redirect-error", handler);
    return () => window.removeEventListener("sp:google-redirect-error", handler);
  }, []);

  const resolveNext = useCallback(() => {
    try {
      const stashed = window.sessionStorage.getItem("sp_auth_next");
      if (stashed) {
        window.sessionStorage.removeItem("sp_auth_next");
        return stashed;
      }
    } catch { /* noop */ }
    return nextTargetFromUrl;
  }, [nextTargetFromUrl]);

  const [mode, setMode] = useState("choose");     // choose | email | email-sent | busy | error
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [emailSentTo, setEmailSentTo] = useState(null);

  // Already signed in → send back to /blog (or ?next=)
  useEffect(() => {
    if (user) navigate(resolveNext(), { replace: true });
  }, [user, navigate, resolveNext]);

  const google = useCallback(async () => {
    setMode("busy");
    setError(null);
    // POPUP FIRST — on cross-origin hosts like socialpulse.blog the
    // Firebase authDomain (voltaic-space-843.firebaseapp.com) stashes
    // the redirect credential in ITS OWN IndexedDB, so getRedirectResult()
    // returns null when we come back and the user gets stuck in a loop
    // right back on this screen. Popup uses postMessage instead — no
    // cross-origin storage dependency — so the credential is delivered
    // directly to this tab.
    //
    // If popup is blocked (Safari private mode, aggressive blockers,
    // COOP:same-origin preview hosts), we fall back to the redirect
    // flow so no one is left stranded.
    try {
      await signInWithGooglePopupProvider(true /* trust */);
      // Success — AuthContext.setUser fires; the top-level useEffect
      // handles navigation to `next`.
      return;
    } catch (popupErr) {
      // eslint-disable-next-line no-console
      console.warn("[BlogAuth] popup sign-in failed, falling back to redirect:", popupErr?.code, popupErr?.message);
      const code = popupErr?.code || "";
      // Blocking codes that mean "just show the error, don't re-attempt":
      if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request" ||
        code === "auth/user-cancelled"
      ) {
        setMode("choose");
        return;
      }
      if (code === "auth/unauthorized-domain") {
        setError(`This site (${typeof window !== "undefined" ? window.location.hostname : ""}) isn't yet in the Firebase project's authorized domains. An admin needs to add it in Firebase Console → Authentication → Settings → Authorized domains.`);
        setMode("error");
        return;
      }
      if (code === "auth/operation-not-allowed") {
        setError("Google sign-in isn't enabled in the Firebase project. Ask an admin to enable it under Authentication → Sign-in method → Google.");
        setMode("error");
        return;
      }
      // Popup blocked / other → try redirect as a fallback.
    }
    try {
      await signInWithGoogleProvider(true /* trust */);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[BlogAuth] Google redirect sign-in failed:", e?.code, e?.message, e);
      const code = e?.code || "";
      const msg =
        code === "auth/unauthorized-domain"
          ? `This site (${typeof window !== "undefined" ? window.location.hostname : ""}) isn't yet in the Firebase project's authorized domains. An admin needs to add it in Firebase Console → Authentication → Settings → Authorized domains.`
        : code === "auth/operation-not-allowed"
          ? "Google sign-in isn't enabled in the Firebase project. Ask an admin to enable it under Authentication → Sign-in method → Google."
        : code === "auth/network-request-failed"
          ? "Network error contacting Google. Check your connection and try again."
        : (e?.response?.data?.detail || e?.message || "Google sign-in didn't complete. Please try again.");
      setError(msg);
      setMode("error");
    }
  }, [signInWithGoogleProvider, signInWithGooglePopupProvider]);

  const magicLink = useCallback(async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setMode("busy");
    setError(null);
    try {
      await sendEmailLink(email.trim());
      setEmailSentTo(email.trim());
      setMode("email-sent");
    } catch (err) {
      setError(err?.message || "Couldn't send the sign-in link. Please try again.");
      setMode("error");
    }
  }, [email, sendEmailLink]);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "#FAFAF7", fontFamily: "Inter, system-ui, sans-serif" }}
      data-testid="blog-auth-page"
    >
      {/* Minimal header — logo only, no main nav */}
      <header className="max-w-[1400px] mx-auto w-full px-6 md:px-10 pt-6">
        <a href="/blog" className="inline-flex items-center gap-3" data-testid="blog-auth-logo">
          <span
            className="w-10 h-10 rounded-full grid place-items-center"
            style={{ background: "linear-gradient(135deg,#0F766E,#134E4A)" }}
            aria-hidden="true"
          >
            <Globe className="w-5 h-5 text-white" />
          </span>
          <span
            className="text-xl"
            style={{ fontFamily: "'Lora', serif", fontWeight: 700, letterSpacing: "-0.01em" }}
          >
            SocialPulse<sup className="text-[10px]" style={{ color: "#0F766E" }}>®</sup>{" "}
            <span style={{ color: "#0F766E" }}>Blog</span>
          </span>
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Card */}
          <div
            className="bg-white rounded-3xl shadow-sm border overflow-hidden"
            style={{ borderColor: "rgba(15,23,42,0.06)" }}
          >
            {/* Hero header */}
            <div
              className="px-8 pt-8 pb-6 text-center border-b"
              style={{ borderColor: "rgba(15,23,42,0.04)" }}
            >
              <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-3">
                Welcome
              </p>
              <h1
                className="text-3xl leading-tight"
                style={{ fontFamily: "'Lora', serif", fontWeight: 600, letterSpacing: "-0.02em" }}
              >
                {mode === "email-sent" ? "Check your email" : "Sign in to SocialPulse"}
              </h1>
              {mode !== "email-sent" && (
                <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                  One account for the whole SocialPulse network — Blog, News, Chats, Places and more.
                </p>
              )}
            </div>

            <div className="px-8 py-8 space-y-3">
              {mode === "busy" && (
                <div className="flex items-center justify-center py-8 gap-2 text-slate-500 text-sm" data-testid="blog-auth-busy">
                  <Loader2 className="w-5 h-5 animate-spin" /> Signing you in…
                </div>
              )}

              {mode === "email-sent" && (
                <div className="text-center py-4" data-testid="blog-auth-email-sent">
                  <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
                  <p className="text-sm text-slate-700 leading-relaxed">
                    We sent a magic link to <br />
                    <strong className="text-slate-900">{emailSentTo}</strong>
                  </p>
                  <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                    Open it on this device to finish signing in. The link expires in 60 minutes.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setMode("choose"); setError(null); }}
                    className="mt-6 text-sm font-semibold"
                    style={{ color: "#0F766E" }}
                    data-testid="blog-auth-back-from-email"
                  >
                    Use a different email ›
                  </button>
                </div>
              )}

              {(mode === "choose" || mode === "error") && (
                <>
                  {/* Google button */}
                  <button
                    type="button"
                    onClick={google}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-full border bg-white text-slate-800 text-sm font-semibold hover:bg-slate-50 transition"
                    style={{ borderColor: "rgba(15,23,42,0.12)" }}
                    data-testid="blog-auth-google"
                  >
                    <img src={GOOGLE_ICON} alt="" className="w-4 h-4" />
                    Continue with Google
                  </button>

                  {/* Divider */}
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px" style={{ background: "rgba(15,23,42,0.08)" }} />
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                      or with email
                    </span>
                    <div className="flex-1 h-px" style={{ background: "rgba(15,23,42,0.08)" }} />
                  </div>

                  {/* Email magic link form */}
                  <form onSubmit={magicLink} className="space-y-3">
                    <label className="block">
                      <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">
                        Email address
                      </span>
                      <div className="relative">
                        <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="w-full pl-10 pr-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          style={{ borderColor: "rgba(15,23,42,0.12)" }}
                          data-testid="blog-auth-email"
                          required
                        />
                      </div>
                    </label>
                    <button
                      type="submit"
                      disabled={!email.trim()}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-white text-sm font-semibold disabled:opacity-40 transition"
                      style={{ background: "#0F766E" }}
                      data-testid="blog-auth-email-submit"
                    >
                      Send me a sign-in link
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </form>

                  {error && (
                    <div
                      className="mt-4 flex items-start gap-2 text-xs px-3 py-2 rounded-lg bg-red-50 text-red-700"
                      data-testid="blog-auth-error"
                    >
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Fine print */}
          <p className="text-center text-xs text-slate-400 mt-5 leading-relaxed max-w-xs mx-auto">
            By continuing you agree to SocialPulse's{" "}
            <a href="/legal/terms" className="underline hover:text-slate-600">Terms</a> and{" "}
            <a href="/legal/privacy" className="underline hover:text-slate-600">Privacy Policy</a>.
          </p>
        </div>
      </main>

      <footer
        className="py-6 text-center text-xs text-slate-400"
        style={{ borderTop: "1px solid rgba(15,23,42,0.05)" }}
      >
        © {new Date().getFullYear()} SocialPulse — Every Place Has a Story. Every Subject Has a Community.
      </footer>
    </div>
  );
}
